import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import { Box, MenuItem, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import CustomSelect from "../../../components/customSelect";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getAllCompanies } from "../../../services/comanyApiFn";
import { getAllSubscriptionsHistoryByRole } from "../../../services/subscriptionApiFn";
import { getAllSupplier } from "../../../services/supplierApiFn";
import { handleOpenSnackBar } from "../../../utils";
import TabBar from "./TabBar";
import { useTranslation } from "react-i18next";
import InvoicePDF from "../../../components/InvoiceTemplate/InvoicePDF";

const headerData = [
  // "transactionId",
  "duration",
  "timeLeft",
  "b2bName",
  "subscriptionName",
  "plan",
  "price",
  "status",
  "action",
];

const tableRows = [
  // "transactionId",
  "package_duration",
  "package_left_days",
  "user_name_supplier",
  "active_package_name",
  "duration",
  "price",
  "subscription_status",
  "download",
];

function Invoices() {
  const dispatch = useDispatch();
  const { t } = useTranslation(["subscription", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const [allHistory, setAllHistory] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const currentLanguage = useSelector((state) => state.languageReducer);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const handleCloseInvoice = () => {
    setIsInvoiceOpen(false);
    setSelectedInvoice(null);
  };

  useEffect(() => {
    if (searchKeys.subscriptionHistoryRole) {
      fetchAllSubscriptionHistory(
        searchKeys.subscriptionHistoryRole,
        searchKeys.subscriptionHistoryUserId
      );
    }
  }, [
    searchKeys.subscriptionHistoryRole,
    searchKeys.subscriptionHistoryUserId,
  ]);

  useEffect(() => {
    if (searchKeys.subscriptionHistoryRole) {
      if (searchKeys.subscriptionHistoryRole === "supplier") {
        fetchAllSuppliers();
      }
      if (searchKeys.subscriptionHistoryRole === "company") {
        fetchAllCompanies();
      }
    }
  }, [searchKeys.subscriptionHistoryRole]);

  const fetchAllSuppliers = async () => {
    try {
      setIsLoading(true);
      const response = await getAllSupplier();
      if (response.data.status === "success") {
        const data = response.data.data.suppliers;
        let updateData = data?.map((val) => ({
          ...val,
          name: val.businessName,
        }));
        setAllClients(updateData);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllCompanies = async () => {
    try {
      setIsLoading(true);
      const params = {
        search: "",
        page: 1,
        limit: 500
      }
      const response = await getAllCompanies(params);
      if (response.data.status === "success") {
        const data = response.data.data.companies;
        setAllClients(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllSubscriptionHistory = async (role, user) => {
    try {
      setIsLoading(true);
      const response = await getAllSubscriptionsHistoryByRole(role, user);
      if (response.data.status === "success") {
        setAllHistory(response.data.data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadInvoice = (row) => {
    // Transform subscription data to invoice format
    const invoiceData = {
      invoiceNumber: row.transactionId || `INV-${Date.now()}`,
      invoiceStartDate: new Date(row.startDate).toLocaleDateString(),
      invoiceEndDate: new Date(row.endDate).toLocaleDateString(),
      customer: {
        name: row.username || "Customer",
        email: row?.email || "customer@example.com",
        phone: row?.contact || "+1 (555) 123-4567",
      },
      subscription: {
        name: row.subscriptionId.name[currentLanguage] || "Subscription Plan",
        duration: row.duration || "",
        price: parseFloat(row.price) || 0,
      },
      paymentStatus: row.status || "Paid",
      transactionId: row.transactionId || `TXN-${Date.now()}`,
    };

    setSelectedInvoice(invoiceData);
    setIsInvoiceOpen(true);
  };

  const handleChangeTab = (_, newValue) => {
    dispatch(
      setSearchKeys({
        ...searchKeys,
        subscriptionHistoryRole: newValue,
        subscriptionHistoryUserId: "All",
      })
    );
    setAllClients([]);
  };

  const renderHeaderAction = () => {
    return (
      <Stack flexGrow={1} direction={{ md: "row" }} justifyContent={"flex-end"}>
        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
        />
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <CustomSelect
          formStyleProps={{
            width: "30%",
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<Person2OutlinedIcon />}
          value={searchKeys.subscriptionHistoryUserId}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                subscriptionHistoryUserId: e.target.value,
              })
            );
          }}
        >
          <MenuItem value={"All"}>{t("all", { ns: "common" })}</MenuItem>
          {allClients?.map((val, ind) => {
            return (
              <MenuItem
                key={ind}
                value={val._id}
                sx={{ textTransform: "capitalize" }}
              >
                {val.name}
              </MenuItem>
            );
          })}
        </CustomSelect>
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("invoices")}
        subtitle={t("downloadInvoicesInfo")}
        // headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        <TabBar
          type={"invoice"}
          value={searchKeys.subscriptionHistoryRole}
          handleChange={handleChangeTab}
        />

        <Box my={4} />

        <DynamicPaginatedTable
          showPagination={true}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allHistory}
          showDownloadBtn={true}
          handleDownloadInvoice={handleDownloadInvoice}
        />
        {allHistory?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound")}!
          </Typography>
        )}

        <InvoicePDF
          open={isInvoiceOpen}
          onClose={handleCloseInvoice}
          invoiceData={selectedInvoice}
        />
      </PaperContainer>
    </>
  );
}

export default Invoices;
